// This file is used to decrypt the content of the acronyms, glossary, flashcards, and questions

import {
    BUNDLE_ACRONYMS,
    BUNDLE_GLOSSARY,
    BUNDLE_FLASHCARDS,
    BUNDLE_QUESTIONS,
    BUNDLE_ARTICLE_COUNTS,
    LOAD_ARTICLES,
  } from "../../content";
  import { GET_APP_DOMAIN_LIST, GET_PRACTICE_TEST } from "../../app_constant";
  import _ from "lodash";
  import CryptoJS from "crypto-js";
  
  const secretKey = process.env.REACT_APP_SECRET_KEY;
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  // Helper function to check if data is encrypted (starts with U2F or similar base64 pattern)
  const isEncrypted = (data) => {
    if (!data || typeof data !== 'string') return false;
    // Encrypted data typically starts with specific patterns
    return /^[A-Za-z0-9+/=]{20,}$/.test(data);
  };
  
  export const GET_ACRONYMS = (SELECTED_BUNDLE) => {
    const acronymsData = BUNDLE_ACRONYMS[SELECTED_BUNDLE];
    if (!acronymsData || _.isEmpty(acronymsData)) {
      console.log('No valid bundle selected or acronyms available for:', SELECTED_BUNDLE);
      return []; 
    }
    
    // If data is already an array (plain JSON), return it directly
    if (Array.isArray(acronymsData)) {
      return acronymsData;
    }
    
    // If data is a string and looks encrypted, decrypt it
    if (typeof acronymsData === 'string' && isEncrypted(acronymsData)) {
      const bytes = CryptoJS.AES.decrypt(acronymsData, secretKey);
      const decryptedText = bytes.toString(CryptoJS.enc.Utf8);
      try {
        const acronymsArray = JSON.parse(decryptedText);
        return acronymsArray;
      } catch (e) {
        console.error("Acronyms data corrupted:", e);
        return [];
      }
    }
    
    // If data is a string but not encrypted, try to parse it as JSON
    if (typeof acronymsData === 'string') {
      try {
        const acronymsArray = JSON.parse(acronymsData);
        return acronymsArray;
      } catch (e) {
        console.error("Acronyms data parsing failed:", e);
        return [];
      }
    }
    
    return [];
  };
  
  export const GET_GLOSSARY = (SELECTED_BUNDLE) => {
    const glossaryData = BUNDLE_GLOSSARY[SELECTED_BUNDLE];
    if (!glossaryData || _.isEmpty(glossaryData)) {
      console.log('No valid bundle selected or glossary available for:', SELECTED_BUNDLE);
      return []; 
    }
    
    // If data is already an array (plain JSON), return it directly
    if (Array.isArray(glossaryData)) {
      return glossaryData;
    }
    
    // If data is a string and looks encrypted, decrypt it
    if (typeof glossaryData === 'string' && isEncrypted(glossaryData)) {
      const bytes = CryptoJS.AES.decrypt(glossaryData, secretKey);
      const decryptedText = bytes.toString(CryptoJS.enc.Utf8);
      try {
        const glossaryArray = JSON.parse(decryptedText);
        return glossaryArray;
      } catch (e) {
        console.error("Glossary data corrupted:", e);
        return [];
      }
    }
    
    // If data is a string but not encrypted, try to parse it as JSON
    if (typeof glossaryData === 'string') {
      try {
        const glossaryArray = JSON.parse(glossaryData);
        return glossaryArray;
      } catch (e) {
        console.error("Glossary data parsing failed:", e);
        return [];
      }
    }
    
    return [];
  };
  
  export const GET_FLASHCARDS = (SELECTED_BUNDLE) => {
    const flashcardsData = BUNDLE_FLASHCARDS[SELECTED_BUNDLE];
    if (!flashcardsData) {
      console.log('No valid bundle selected or flashcards available for:', SELECTED_BUNDLE);
      return [];
    }
    
    // If data is already an array (plain JSON), return it directly
    if (Array.isArray(flashcardsData)) {
      return flashcardsData;
    }
    
    // If data is a string and looks encrypted, decrypt it
    if (typeof flashcardsData === 'string' && isEncrypted(flashcardsData)) {
      const bytes = CryptoJS.AES.decrypt(flashcardsData, secretKey);
      const decryptedText = bytes.toString(CryptoJS.enc.Utf8);
      try {
        const flashcardsArray = JSON.parse(decryptedText);
        return flashcardsArray;
      } catch (e) {
        console.error("Flashcards data corrupted:", e);
        return [];
      }
    }
    
    // If data is a string but not encrypted, try to parse it as JSON
    if (typeof flashcardsData === 'string') {
      try {
        const flashcardsArray = JSON.parse(flashcardsData);
        return flashcardsArray;
      } catch (e) {
        console.error("Flashcards data parsing failed:", e);
        return [];
      }
    }
    
    return [];
  };

  // Async: article bodies live in a lazily-loaded chunk (see LOAD_ARTICLES). Callers
  // must await this. For counts at startup use BUNDLE_ARTICLE_COUNTS instead.
  export const GET_ARTICLES = async (SELECTED_BUNDLE) => {
    const articlesData = await LOAD_ARTICLES(SELECTED_BUNDLE);
    if (!articlesData) {
      console.log('No valid bundle selected or articles available for:', SELECTED_BUNDLE);
      return [];
    }

    // If data is already an array (plain JSON), return it directly
    if (Array.isArray(articlesData)) {
      return articlesData;
    }
    
    // If data is a string and looks encrypted, decrypt it
    if (typeof articlesData === 'string' && isEncrypted(articlesData)) {
      const bytes = CryptoJS.AES.decrypt(articlesData, secretKey);
      const decryptedText = bytes.toString(CryptoJS.enc.Utf8);
      try {
        const articlesArray = JSON.parse(decryptedText);
        return articlesArray;
      } catch (e) {
        console.error("Articles data corrupted:", e);
        return [];
      }
    }
    
    // If data is a string but not encrypted, try to parse it as JSON
    if (typeof articlesData === 'string') {
      try {
        const articlesArray = JSON.parse(articlesData);
        return articlesArray;
      } catch (e) {
        console.error("Articles data parsing failed:", e);
        return [];
      }
    }
    
    return [];
  };

  export const GET_LOCAL_QUESTIONS = (SELECTED_BUNDLE) => {
    const questionsData = BUNDLE_QUESTIONS[SELECTED_BUNDLE];
    
    if (!questionsData) {
      console.log('No valid bundle selected or questions available for:', SELECTED_BUNDLE);
      return [];
    }
    
    // If data is already an array (plain JSON), return it directly
    if (Array.isArray(questionsData)) {
      return questionsData;
    }
    
    // If data is a string and looks encrypted, decrypt it
    if (typeof questionsData === 'string' && isEncrypted(questionsData)) {
      const bytes = CryptoJS.AES.decrypt(questionsData, secretKey);
      const decryptedText = bytes.toString(CryptoJS.enc.Utf8);
      try {
        const questionsArray = JSON.parse(decryptedText);
        return questionsArray;
      } catch (e) {
        console.error("Questions data corrupted:", e);
        return [];
      }
    }
    
    // If data is a string but not encrypted, try to parse it as JSON
    if (typeof questionsData === 'string') {
      try {
        const questionsArray = JSON.parse(questionsData);
        return questionsArray;
      } catch (e) {
        console.error("Questions data parsing failed:", e);
        return [];
      }
    }
    
    return [];
  };

  export const GET_DOMAIN_NAME = (SELECTED_BUNDLE, topic_id) => {
    const APP_DOMAIN_LIST = GET_APP_DOMAIN_LIST(SELECTED_BUNDLE);
    const array = APP_DOMAIN_LIST.find((e) => e.id == topic_id);
    return array?.domain_name ?? "";
  };

/**
 * Get exam statistics for all available exams
 * @returns {Array} Array of objects containing exam stats with key, title, and counts for questions, flashcards, articles, glossary, and acronyms
 */
export const getExamStats = () => {
  // Import APP_BUNDLES from app_constant
  const { APP_BUNDLES } = require("../../app_constant");

  return APP_BUNDLES.map(bundle => {
    const examKey = bundle.key;
    const examTitle = bundle.title;

    // Get content for this exam using existing functions
    const questions = GET_LOCAL_QUESTIONS(examKey);
    const flashcards = GET_FLASHCARDS(examKey);
    const glossary = GET_GLOSSARY(examKey);
    const acronyms = GET_ACRONYMS(examKey);
    const practiceTests = GET_PRACTICE_TEST(examKey);

    return {
      key: examKey,
      title: examTitle,
      questions: questions ? questions.length : 0,
      flashcards: flashcards ? flashcards.length : 0,
      // Article bodies load lazily; use the static count so stats stay synchronous.
      articles: BUNDLE_ARTICLE_COUNTS[examKey] || 0,
      glossary: glossary ? glossary.length : 0,
      acronyms: acronyms ? acronyms.length : 0,
      practiceTests: practiceTests ? practiceTests.length : 0
    };
  });
};
  